import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Button, DatePicker, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/store";
import CaretDownIcon from "../../../assets/icons/CaretDownIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import {
  bloodGroups,
  dbDateFormat,
  mailRegex,
} from "../../../constants/constants";
import { useGetMeQuery } from "../../../features/auth/authApi";
import {
  useCreateEmployeeMutation,
  useFileUploadMutation,
  useGetAllDepartmentQuery,
  useGetAllEmployeesQuery,
  useGetAllSubDepartmentQuery,
  useGetEmployeeDetailsQuery,
  useGetRoleListQuery,
  useGetSubDepartmentByIdGroupChatQuery,
  useUpdateEmployeeByIdMutation,
} from "../../../features/superAdmin/employeeService/employeeManagementService";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { toast } from "../../../lib/toast";
import { GetProfileResponseType, ModalComponentProps } from "../../../types";
import { isValidTLD, selectStringOption } from "../../../utils/utils";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";
import SuccessModal from "../RoleManagement/SuccessModal";

const { Option } = Select;

export default function AddNewUserModal({
  handleClose,
  type,
  data: userData,
  ...rest
}: ModalComponentProps) {
  const user = useAppSelector((state) => state.auth);
  const [lineManagerLabel, setLineManagerLabel] = useState("");

  const { data: profileInfo, isLoading: profileLoading } = useGetMeQuery(
    user?.user?.id,
  );
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    number | null
  >(null);

  const [images, setImages] = useState<File[]>([]);

  const { open, handleOpen, handleClose: handleNestedModalClose } = useModal();
  const [form] = Form.useForm();

  const {
    isError: allDepartmentIsError,
    error: allDepartmentError,
    data: allDepartmentData,
    isLoading: depLoading,
  } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000000000,
      status: "active",
    },
  });
  const {
    isError: allSubDepartmentIsError,
    error: allSubDepartmentError,
    data: allSubDepartmentData,
  } = useGetAllSubDepartmentQuery({
    params: {
      page: 1,
      limit: 1000,
    },
  });

  const id = selectedDepartmentId ? selectedDepartmentId : skipToken;

  const {
    isError: subDepartmentIsError,
    error: subDepartmentError,
    data: subDepartmentData,
  } = useGetSubDepartmentByIdGroupChatQuery(id);

  const {
    data: roleData,
    isError: isRoleDataError,
    error: roleDataError,
  } = useGetRoleListQuery({});

  const [
    createEmployee,
    {
      isSuccess: isCreateEmployeeSuccess,
      isError: isCreateEmployeeError,
      error: createEmployeeError,
      isLoading: isCreateEmployeeLoading,
    },
  ] = useCreateEmployeeMutation();

  const [
    updateEmployeeById,
    {
      // data: updateEmployeeByIdData,
      isSuccess: isUpdateEmployeeByIdSuccess,
      isError: isUpdateEmployeeByIdError,
      error: updateEmployeeByIdError,
      isLoading: isUpdateEmployeeByIdLoading,
    },
  ] = useUpdateEmployeeByIdMutation();
  const {
    isLoading,
    isError,
    error,
    data: allEmployeesData,
  } = useGetAllEmployeesQuery({
    params: {
      page: 1,
      limit: 100,
    },
  });
  const {
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
    data: detailsData,
  } = useGetEmployeeDetailsQuery(userData?.id, {
    skip: type !== "UPDATE",
  });

  const [fileUpload, { error: fileUploadError, isError: fileUploadIsError }] =
    useFileUploadMutation();
  useError(fileUploadIsError, fileUploadError, "Upload photo fail");
  // useError(fileUploadIsError, fileUploadError, "Upload photo fail");
  useError(isError, error, "User list get fail");
  useError(isDetailsError, detailsError, "User details get fail");
  useError(
    subDepartmentIsError,
    subDepartmentError,
    "Get sub department list fail",
  );

  useError(
    allDepartmentIsError,
    allDepartmentError,
    "Get all department list fail",
  );
  useError(
    allSubDepartmentIsError,
    allSubDepartmentError,
    "Get all sub department list fail",
  );
  useError(isRoleDataError, roleDataError, "Get role list fail");
  useError(isCreateEmployeeError, createEmployeeError, "User create fail");
  useError(
    isUpdateEmployeeByIdError,
    updateEmployeeByIdError,
    "User update fail",
  );

  const handleFinish = (values: any) => {
    const { company, line_manager, date_of_joining, ...rest } = values;

    const newValues = {
      ...rest,
      date_of_joining: date_of_joining
        ? date_of_joining.format(dbDateFormat)
        : undefined,
      company: Number(company),
      line_manager: Number(line_manager),
      // nid_front_image: NID_front,
      // nid_back_image: NID_back,
    };

    if (type === "UPDATE") {
      updateEmployeeById({ id: userData?.id, data: newValues });
    } else {
      createEmployee(newValues);
    }
  };

  const processImageUpload = (file: File, key: string) => {
    const maxFileSize = 32 * 1024 * 1024; // 32MB
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

    if (!allowedTypes.includes(file.type)) {
      toast("error", `Only JPG and PNG image files are allowed.`);
      return;
    }

    if (file.size > maxFileSize) {
      toast("error", `"${file.name}" exceeds max file size of 32MB.`);
      return;
    }

    setImages([file]);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    fileUpload(formData)
      .unwrap()
      .then((res: any) => {
        if (res?.Location) {
          form.setFieldsValue({
            [key]: res.Location,
          });
        }
      })
      .catch((err) => {
        console.error("Image upload failed", err);
      });
  };

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const file = files[0]; // Only one image
    processImageUpload(file, key);

    // Clear input value to allow re-selecting the same file
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>, key: string) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    processImageUpload(file, key);
  };

  const emp_photo = Form.useWatch(`emp_photo`, form);
  const NID_front = Form.useWatch(`NID_front`, form);
  const NID_back = Form.useWatch(`NID_back`, form);

  const removeImage = (key: string) => {
    form.setFieldsValue({
      [key]: undefined,
    });
  };

  useEffect(() => {
    if (isCreateEmployeeSuccess || isUpdateEmployeeByIdSuccess) {
      handleOpen({ type: "VIEW" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateEmployeeSuccess, isUpdateEmployeeByIdSuccess]);

  useEffect(() => {
    if (type === "UPDATE") {
      const viewData = detailsData?.data;

      const addressParts = viewData?.personalInfo?.address
        .split(",")
        .map((part) => part.trim());

      form.setFieldsValue({
        name: viewData?.officialInfo?.name,
        emp_photo: viewData?.personalInfo?.pro_pic,
        email: viewData?.personalInfo?.email,
        phone: viewData?.personalInfo?.phone,
        NID_front: viewData?.personalInfo?.nid_front_image,
        NID_back: viewData?.personalInfo?.nid_back_image,
        company: viewData?.company_id,
        district: viewData?.personalInfo?.district,
        city: viewData?.personalInfo?.city,
        department: viewData?.officialInfo?.department_id,
        sub_department: viewData?.officialInfo?.sub_department_id,
        line_manager: viewData?.officialInfo?.line_manager,
        designation: viewData?.officialInfo?.designation,
        given_employee_id: String(viewData?.officialInfo?.giver_employee_id),
        grade: viewData?.officialInfo?.grade,
        gender: viewData?.personalInfo?.gender?.toLowerCase(),
        nationality: viewData?.personalInfo?.nationality,
        address: viewData?.personalInfo?.address,
        blood_group: viewData?.personalInfo?.blood_group,
        date_of_joining: viewData?.officialInfo?.date_of_joining
          ? dayjs(viewData?.officialInfo?.date_of_joining, dbDateFormat)
          : undefined,
        employee_type: viewData?.officialInfo?.employee_type,
        role_id: viewData?.officialInfo?.role_id,
      });
      setSelectedDepartmentId(
        viewData?.officialInfo.department_id as number,
      );
    }
  }, [detailsData?.data, form, type]);

  useEffect(() => {
    const companyId = (profileInfo as unknown as GetProfileResponseType)?.data?.company_id;
    if (companyId) {
      form.setFieldValue("company", companyId);
    }
  }, [profileInfo, form]);

  const handleCloseCreate = () => {
    handleClose();
    handleNestedModalClose();
  };

  return (
    <CustomModal
      title={type === "UPDATE" ? "Update User Info" : "Add new User"}
      {...rest}
      handleClose={handleClose}
      width={500}
      footer={null}
    >
      {isDetailsLoading ? (
        <Loader />
      ) : (
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="name"
            label="Employee Name"
            rules={[
              { required: true, message: "Please enter employee name" },
            ]}
          >
            <Input placeholder="Employee name" allowClear />
          </Form.Item>

          <Form.Item
            name="emp_photo"
            label="User Photo"
            // rules={[{ required: true }]}
          >
            <div className="flex items-center flex-wrap gap-4 mt-2">
              {emp_photo ? (
                <div className="relative w-44 h-32">
                  <img
                    src={emp_photo}
                    alt="upload"
                    className="w-full h-full object-cover rounded-md border border-[#D7DFE9]"
                  />
                  <div
                    className="absolute top-1 right-1 cursor-pointer pt-1 pr-1"
                    onClick={() => removeImage("emp_photo")}
                  >
                    <CloseIcon />
                  </div>
                </div>
              ) : (
                <label
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, "emp_photo")}
                  htmlFor="imageUpload"
                  className="w-full border border-dashed border-[#AFBACA] rounded-md p-4 text-center text-[#8897AE] cursor-pointer"
                >
                  <div className="text-center">
                    <span className="text-[#8897AE] text-sm font-normal leading-normal">
                      Drag and Drop
                      <br />
                      Or
                      <br />
                    </span>
                    <span className="text-[#3D4A5C] text-sm font-medium underline leading-normal">
                      Upload Image
                    </span>
                  </div>
                  <input
                    id="imageUpload"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleImageChange(e, "emp_photo")}
                  />
                </label>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="email"
            label="E-mail"
            validateFirst
            rules={[
              {
                required: true,
                message: "Please enter email",
              },
              {
                validator: async (_: any, value: string) => {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (!mailRegex.test(value)) {
                    return Promise.reject(
                      new Error("Please enter a valid email"),
                    );
                  }

                  if (!isValidTLD(value)) {
                    return Promise.reject(
                      new Error("Please enter a valid TLD"),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Email" allowClear />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
            validateFirst
            rules={[
              {
                validator: async (_: any, value: string) => {
                  if (!value) {
                    return Promise.reject(
                      new Error("Please enter phone number"),
                    );
                  }
                  // if (!value) {
                  //   return Promise.resolve();
                  // }

                  const phoneRegex = /^(\+88)?(01[3-9])[0-9]{8}$/;
                  if (!phoneRegex.test(value)) {
                    return Promise.reject(
                      new Error(
                        "Please enter a valid Bangladeshi phone number",
                      ),
                    );
                  }
                },
              },
            ]}
          >
            <Input placeholder="Phone" allowClear />
          </Form.Item>

          <Form.Item
            name="district"
            label="District"
            // rules={[{ required: true, message: "Please enter district" }]}
          >
            <Input placeholder="District" />
          </Form.Item>

          <Form.Item
            name="city"
            label="City"
            // rules={[{ required: true, message: "Please enter city" }]}
          >
            <Input placeholder="Enter city" />
          </Form.Item>

          <Form.Item
            name="NID_front"
            label="National ID Card (Front)"
            // rules={[{ required: true }]}
          >
            <div className="flex items-center flex-wrap gap-4 mt-2">
              {NID_front ? (
                <div className="relative w-44 h-32">
                  <img
                    src={NID_front}
                    alt="upload"
                    className="w-full h-full object-cover rounded-md border border-[#D7DFE9]"
                  />
                  <div
                    className="absolute top-1 right-1 cursor-pointer pt-1 pr-1"
                    onClick={() => removeImage("NID_front")}
                  >
                    <CloseIcon />
                  </div>
                </div>
              ) : (
                <label
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, "NID_front")}
                  htmlFor="imageUpload"
                  className="w-full border border-dashed border-[#AFBACA] rounded-md p-4 text-center text-[#8897AE] cursor-pointer"
                >
                  <div className="text-center">
                    <span className="text-[#8897AE] text-sm font-normal leading-normal">
                      Drag and Drop
                      <br />
                      Or
                      <br />
                    </span>
                    <span className="text-[#3D4A5C] text-sm font-medium underline leading-normal">
                      Upload File
                    </span>
                  </div>
                  <input
                    id="imageUpload"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleImageChange(e, "NID_front")}
                  />
                </label>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="NID_back"
            label="National ID Card (Back)"
            // rules={[{ required: true }]}
          >
            <div className="flex items-center flex-wrap gap-4 mt-2">
              {NID_back ? (
                <div className="relative w-44 h-32">
                  <img
                    src={NID_back}
                    alt="upload"
                    className="w-full h-full object-cover rounded-md border border-[#D7DFE9]"
                  />
                  <div
                    className="absolute top-1 right-1 cursor-pointer pt-1 pr-1"
                    onClick={() => removeImage("NID_back")}
                  >
                    <CloseIcon />
                  </div>
                </div>
              ) : (
                <label
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, "NID_back")}
                  htmlFor="imageUpload"
                  className="w-full border border-dashed border-[#AFBACA] rounded-md p-4 text-center text-[#8897AE] cursor-pointer"
                >
                  <div className="text-center">
                    <span className="text-[#8897AE] text-sm font-normal leading-normal">
                      Drag and Drop
                      <br />
                      Or
                      <br />
                    </span>
                    <span className="text-[#3D4A5C] text-sm font-medium underline leading-normal">
                      Upload File
                    </span>
                  </div>
                  <input
                    id="imageUpload"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleImageChange(e, "NID_back")}
                  />
                </label>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="company"
            label="Company"
            rules={[{ required: true, message: "Please enter company" }]}
          >
            <Select
              disabled
              placeholder="Select"
              suffixIcon={<CaretDownIcon />}
              options={[
                {
                  label: "Upscale",
                  value: 1,
                },
                {
                  label: "Sheba",
                  value: 2,
                },
                {
                  label: "Sheba",
                  value: 38,
                },
              ]}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true, message: "Please enter department" }]}
          >
            <Select
              placeholder="Select"
              suffixIcon={<CaretDownIcon />}
              options={allDepartmentData?.data.map((dep: any) => ({
                label: dep.name,
                value: dep.id,
              }))}
              onChange={(value) => setSelectedDepartmentId(value)}
              allowClear
            ></Select>
          </Form.Item>

          <Form.Item
            name="sub_department"
            label="Sub Department"
            rules={[{ required: true, message: "Please enter sub department" }]}
          >
            <Select
              placeholder="Select"
              suffixIcon={<CaretDownIcon />}
              options={subDepartmentData?.data.map((dep: any) => ({
                label: dep.name,
                value: dep.id,
              }))}
              allowClear
              disabled={!selectedDepartmentId}
              loading={!depLoading}
            ></Select>
          </Form.Item>

          <Form.Item
            name="line_manager"
            label="Line Manager"
            rules={[{ required: true, message: "Please enter line manager" }]}
          >
            <Select
              placeholder="Select"
              // mode="combobox"
              suffixIcon={<CaretDownIcon />}
              allowClear
              loading={isLoading}
              options={allEmployeesData?.data?.employees?.map((emp: any) => ({
                label: emp.officialInfo?.name,
                value: emp.id,
              }))}
            />
            {/* <AutoComplete
              options={allEmployeesData?.data?.employees?.map((emp: any) => ({
                label: emp.officialInfo?.name,
                value: emp.id,
              }))}
              placeholder="Select Line Manager"
              value={lineManagerLabel}
              allowClear
              // Let AutoComplete filter options internally
              filterOption={(inputValue, option) =>
                option?.label.toLowerCase().includes(inputValue.toLowerCase())
              }
              onSelect={(value, option) => {
                // When user selects an option:
                // 1. Set input to option.label (employee name)
                setLineManagerLabel(option.label);
                // 2. Set form field value to the employee id
                form.setFieldsValue({ line_manager: value });
              }}
            /> */}
          </Form.Item>

          <Form.Item
            name="designation"
            label="Designation"
            rules={[{ required: true, message: "Please enter designation" }]}
          >
            <Input placeholder="Designation" allowClear />
          </Form.Item>

          {/* <Form.Item
            name="given_employee_id"
            label="Employee ID"
            rules={[
              { required: true, message: "Please enter employee id" },
              {
                pattern: /^[a-zA-Z0-9._-]+$/,
                message:
                  "Only letters, numbers, dots (.), dashes (-), and underscores (_) are allowed",
              },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  // Check if value is only numbers (without any letter)
                  if (/^\d+$/.test(value)) {
                    if (Number(value) < 0) {
                      return Promise.reject(
                        new Error("Employee ID cannot be negative")
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Employee ID" allowClear />
          </Form.Item> */}

          <Form.Item
            name="given_employee_id"
            label="Employee ID"
            rules={[
              { required: true, message: "Please enter employee id" },
              {
                pattern: /^(?!.*[._-]{2})[a-zA-Z0-9]+([._-]?[a-zA-Z0-9]+)*$/,
                message:
                  "Only letters, numbers, dots (.), dashes (-), and underscores (_) are allowed. No consecutive special characters.",
              },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  // Check if it's numeric only
                  if (/^\d+$/.test(value)) {
                    if (Number(value) < 0) {
                      return Promise.reject(
                        new Error("Employee ID cannot be negative"),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Employee ID" allowClear />
          </Form.Item>

          <Form.Item
            name="grade"
            label="Grade"
            rules={[
              { required: true, message: "Please enter grade" },
              {
                validator: (_, value) => {
                  if (!value && value !== 0) {
                    return Promise.resolve();
                  }
                  if (isNaN(value)) {
                    return Promise.reject(new Error("Grade must be a number"));
                  }
                  if (Number(value) < 0) {
                    return Promise.reject(
                      new Error("Grade cannot be negative"),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Grade" allowClear />
          </Form.Item>

          <Form.Item
            name="gender"
            label="Gender"
            rules={[{ required: true, message: "Please enter gender" }]}
          >
            <Select
              placeholder="Select"
              suffixIcon={<CaretDownIcon />}
              allowClear
            >
              <Option value="male">Male</Option>
              <Option value="female">Female</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="nationality"
            label="Nationality"
            rules={[{ required: true, message: "Please enter nationality" }]}
          >
            <Input placeholder="Nationality" allowClear />
          </Form.Item>

          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: "Please enter address" }]}
          >
            <Input placeholder="Address" allowClear />
          </Form.Item>

          <Form.Item
            name="blood_group"
            label="Blood Group"
            rules={[{ required: true, message: "Please enter blood group" }]}
          >
            <Select
              placeholder="Select"
              suffixIcon={<CaretDownIcon />}
              allowClear
              options={selectStringOption(bloodGroups)}
            ></Select>
          </Form.Item>

          <Form.Item
            name="date_of_joining"
            label="Joining Date"
            rules={[
              { required: true, message: "Please enter joining date" },
              {
                validator(_, value) {
                  if (!value) {
                    return Promise.resolve();
                  }

                  const today = dayjs().startOf("day");
                  const selectedDate = dayjs(value).startOf("day");

                  if (selectedDate.isAfter(today)) {
                    return Promise.reject(
                      new Error("Joining date cannot be a future date"),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              allowClear
              format="YYYY-MM-DD"
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
            />
          </Form.Item>

          <Form.Item
            name="employee_type"
            label="Employee Type"
            rules={[{ required: true, message: "Please enter employee type" }]}
          >
            <Select
              placeholder="Select"
              suffixIcon={<CaretDownIcon />}
              allowClear
            >
              <Option value="full time">Full-Time</Option>
              <Option value="part time">Part-Time</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="role_id"
            label="Role"
            rules={[{ required: true, message: "Please enter role" }]}
          >
            <Select
              placeholder="Select"
              suffixIcon={<CaretDownIcon />}
              options={roleData?.data.map((dep: any) => ({
                label: dep.role_name,
                value: dep.id,
              }))}
              allowClear
            ></Select>
          </Form.Item>

          <div className="flex justify-between gap-2">
            <Button htmlType="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isCreateEmployeeLoading || isUpdateEmployeeByIdLoading}
            >
              {type === "UPDATE" ? "Update" : "Submit"}
            </Button>
          </div>
        </Form>
      )}

      {open.type === "VIEW" && (
        <SuccessModal
          open={open.open}
          handleClose={handleCloseCreate}
          heading={
            type === "UPDATE"
              ? "User updated successfully."
              : "New User added Successfully"
          }
          subHeading={
            type === "UPDATE"
              ? "You have successfully updated a user."
              : "You have successfully added a user."
          }
        />
      )}
    </CustomModal>
  );
}
